import { ReactPortal } from '../reactPortal'
import { FC, useState, useRef, ReactElement, ReactNode, useEffect, memo } from 'react'
import classes from '../../styles/tooltip.module.scss'
import cn from 'classnames'
import { OutClick } from '../outClick'

export type TPlacement = 'top-middle' | 'bottom-middle' | 'right-middle' | 'left-middle' | 'bottom-right'
export type TTriggerMode = 'hover' | 'click'

export const transformConfig: { [p: string]: string } = {
  'top-middle': 'translate(-50%, -100%)',
  'bottom-middle': 'translate(-50%, 0%)',
  'right-middle': 'translate(0%, -50%)',
  'left-middle': 'translate(-100%, -50%)',
  'bottom-right': 'translate(-25%, 20%)',
}

export const PortalTooltip: FC<{
  children: ReactElement
  placement?: TPlacement
  showDelay?: number
  stayTooltipOnHover?: boolean
  hideDelay?: number
  hideTooltip?: boolean
  tooltipContent: ReactNode
  offset?: number
  handleClose?: () => void
  classes?: {
    contentClassName?: string
    tooltipWrapperClassName?: string
    childrenWrapperClassName?: string
  }
  triangle?: boolean
  showOnMobile?: boolean
  triggerMode?: TTriggerMode // Пропс для выбора режима
}> = memo(
  ({
    children,
    placement = 'top-middle',
    showDelay = 50,
    hideDelay = 0,
    hideTooltip,
    tooltipContent,
    offset = 8,
    handleClose = () => {},
    classes: classNames,
    triangle = true,
    triggerMode = 'hover', // Значение по умолчанию — 'hover'
  }) => {
    const { contentClassName, tooltipWrapperClassName, childrenWrapperClassName } = classNames || {}
    const [pos, setPos] = useState({ x: 0, y: 0, active: false, transform: transformConfig[placement], placement })
    const ref = useRef<HTMLDivElement>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const showTooltip = () => {
      if (!ref.current || pos.active || !tooltipContent || typeof window === 'undefined') return
      timeoutRef.current && clearTimeout(timeoutRef.current)
      const rect = ref.current.getBoundingClientRect()
      let x = rect.x + ref.current.clientWidth / 2
      let y = rect.y + window.scrollY

      const spaceBelow = window.innerHeight - rect.bottom
      const effectivePlacement =
        (placement === 'bottom-middle' || placement === 'bottom-right') && spaceBelow < 320
          ? 'top-middle'
          : placement

      if (effectivePlacement === 'bottom-middle' || effectivePlacement === 'bottom-right') y += ref.current.clientHeight
      if (effectivePlacement === 'right-middle') {
        x = rect.x + ref.current.clientWidth
        y += ref.current.clientHeight / 2
      }
      if (effectivePlacement === 'left-middle') {
        x = rect.x
        y += ref.current.clientHeight / 2
      }

      const yWithOffset =
        effectivePlacement === 'bottom-middle'
          ? y + offset
          : effectivePlacement === 'top-middle'
            ? y - offset
            : y
      const xWithOffset =
        effectivePlacement === 'right-middle' ? x + offset : effectivePlacement === 'left-middle' ? x - offset : x

      setPos((p) => ({
        ...p,
        x: xWithOffset,
        y: yWithOffset,
        active: true,
        transform: transformConfig[effectivePlacement],
        placement: effectivePlacement,
      }))
    }

    const hideTooltipHandler = () => {
      timeoutRef.current && clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setPos((p) => ({ ...p, active: false }))
        if (pos.x && pos.y) setPos((p) => ({ ...p, x: 0, y: 0 }))
      }, hideDelay)
    }

    const toggleTooltip = (e: any) => {
      e.stopPropagation()
      if (!pos.active) {
        showTooltip()
      } else {
        setPos((p) => ({ ...p, active: false }))
      }
    }

    // Скрываем tooltip при начале прокрутки
    useEffect(() => {
      if (!pos.active) return
      const handleScroll = () => {
        setPos((p) => ({ ...p, active: false }))
        handleClose()
      }
      window.addEventListener('scroll', handleScroll, true)
      return () => {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }, [pos.active, handleClose])

    useEffect(() => {
      if (!tooltipContent) return
      if (!pos.x && !pos.y && !pos.active) timeoutRef.current && clearTimeout(timeoutRef.current)
      if (hideTooltip && pos.active) setPos((p) => ({ ...p, x: 0, y: 0, active: false }))
    }, [hideTooltip, pos, tooltipContent])

    return (
      <div
        className={cn(
          'tooltipChildrenWrapper',
          classes.tooltipChildrenWrapper,
          childrenWrapperClassName && childrenWrapperClassName
        )}
        ref={ref}
        onMouseEnter={triggerMode === 'hover' ? showTooltip : undefined}
        onMouseLeave={triggerMode === 'hover' ? hideTooltipHandler : undefined}
        onClick={triggerMode === 'click' ? toggleTooltip : undefined}
      >
        <ReactPortal wrapperId="tooltipWrapper">
          {pos.active && (
            <OutClick
              onClick={() => {
                handleClose()
                setPos((p) => ({ ...p, active: false }))
              }}
            >
              <div
                className={cn(classes.portalTooltipWrapper, tooltipWrapperClassName && tooltipWrapperClassName)}
                style={{
                  transform: pos.transform,
                  opacity: pos.active ? 1 : 0,
                  left: pos.x,
                  top: pos.y,
                }}
              >
                <div
                  className={cn(
                    classes.portalTooltipContent,
                    triangle && classes.triangledContent,
                    pos.placement,
                    contentClassName
                  )}
                >
                  {tooltipContent}
                </div>
              </div>
            </OutClick>
          )}
        </ReactPortal>

        {children}
      </div>
    )
  }
)
